import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../../../firebase/config';
import { 
    doc, 
    getDoc, 
    getDocs, 
    query, 
    where, 
    collection,
    collectionGroup // ◄ IMPORTANTE: Agregado para solucionar el error de ESLint 'no-undef'
} from 'firebase/firestore';

// Importamos la utilidad de persistencia centralizada
import { saveToFirestore } from '../../../firebase/cleanPayload'; 

// Importamos los módulos especializados
import { 
    INITIAL_STATE, 
    mapMonedaToDB, 
    getNormalizedPatentes,
    calculatePolicyStatus 
} from './policyUtils';

// Importaciones del Contexto y Utilidades Centralizadas (Pilares del Canvas)
import { useSegurosData } from '../../../context/SegurosDataContext';
import { formatCurrencyForInput, normalizeString } from '../../../utils/segurosUtils';
import { usePolicyLogic } from './usePolicyLogic';
import { savePolicy } from './policyService';

/**
 * Hook Principal: usePolizaForm
 * Orquestador central de pólizas patrimoniales.
 * Consume los datos maestros de forma global, minimizando llamadas de Firebase.
 */
export const usePolizaForm = (clienteActivo, mode, initialData, tipoCarga, onNotifySuccess) => {
    // --- 1. ESTADOS LOCALES ---
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [canalObj, setCanalObj] = useState(null);
    const [subcanalObj, setSubcanalObj] = useState(null);
    const [riesgoObj, setRiesgoObj] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isHydrated, setIsHydrated] = useState(false);
    const [fetchedClient, setFetchedClient] = useState(null);
    const [packInfos, setPackInfos] = useState({});
    const [dbSubcanales, setDbSubcanales] = useState([]);

    const activeClient = clienteActivo || fetchedClient;

    // --- 2. CONSUMO DE DATOS MAESTROS DESDE EL CONTEXTO GLOBAL ---
    const { dbMasters } = useSegurosData();

    // --- 3. MOTOR DE LÓGICA Y AUTOMATIZACIÓN (UI/Valores) ---
    const logic = usePolicyLogic({
        formData,
        setFormData,
        dbMasters,
        dbSubcanales,
        activeClient,
        riesgoObj,
        canalObj,
        subcanalObj,
        mode,
        initialData,
        isHydrated
    });

    // --- 4. RECUPERACIÓN ASÍNCRONA DE DATOS DE CLIENTE SI FALTA ---
    useEffect(() => {
        const loadClientDataIfMissing = async () => {
            const cId = clienteActivo?.id || initialData?.clienteId;
            if (cId && (!activeClient || activeClient.id !== cId)) {
                try {
                    const cSnap = await getDoc(doc(db, 'public/data/clientes', cId));
                    if (cSnap.exists()) {
                        setFetchedClient({ id: cSnap.id, ...cSnap.data() });
                    }
                } catch (e) { 
                    console.error("Error recuperando cliente:", e); 
                }
            }
        };
        loadClientDataIfMissing();
    }, [clienteActivo, initialData?.clienteId, activeClient]);

    // --- 5. CARGA DINÁMICA DE SUBCANALES EN BASE AL CANAL ID SELECCIONADO ---
    useEffect(() => {
        const fetchSubcanales = async () => {
            const id = formData.canalNombreId;
            if (!id) {
                setDbSubcanales([]);
                return;
            }
            try {
                const snap = await getDocs(query(collection(db, 'public/data/subcanales'), where('canalId', '==', id)));
                const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setDbSubcanales(list);

                if (formData.subcanalNombreId) {
                    const found = list.find(s => s.id === formData.subcanalNombreId);
                    if (found) setSubcanalObj(found);
                }
            } catch (error) {
                console.error("Error al obtener subcanales para el canal seleccionado:", error);
            }
        };

        fetchSubcanales();
    }, [formData.canalNombreId, formData.subcanalNombreId]);

    // --- 6. PROCESO DE HIDRATACIÓN E INICIO DE RENOVACIÓN ---
    useEffect(() => {
        if (dbMasters.canales.length === 0 || isHydrated) return;

        const hydrate = async () => {
            try {
                if (initialData?.id) {
                    const { id, ...data } = initialData;
                    
                    // Reconciliación de Canal (Soporte Legacy)
                    const canalNombreLegacy = data.canalNombre || data.subcanal || '';
                    const canalNombreIdLegacy = data.canalNombreId || '';

                    let foundCanal = null;
                    if (canalNombreIdLegacy) {
                        foundCanal = dbMasters.canales.find(c => c.id === canalNombreIdLegacy);
                    } else if (canalNombreLegacy) {
                        foundCanal = dbMasters.canales.find(c => 
                            normalizeString(c.nombreDisplay || c.nombre) === normalizeString(canalNombreLegacy)
                        );
                    }

                    setCanalObj(foundCanal || null);

                    // Normalización de Riesgo
                    if (data.riesgo) {
                        const rFound = dbMasters.riesgos.find(r => normalizeString(r.nombre) === normalizeString(data.riesgo));
                        setRiesgoObj(rFound || null);
                    }

                    const patentesNormalizadas = getNormalizedPatentes(data);

                    // Preparación del Form Data
                    const baseData = {
                        ...data,
                        type: 'patrimoniales',
                        canal: foundCanal?.canal || data.canal || '',
                        canalNombre: canalNombreLegacy,
                        canalNombreId: foundCanal?.id || canalNombreIdLegacy,
                        companiaId: data.companiaId || '',
                        riesgoId: data.riesgoId || '',
                        patentes: patentesNormalizadas,
                    };

                    const fechaCargaOriginal = data.fechaCarga || initialData.fechaCarga || new Date().toISOString().split('T')[0];

                    if (mode === 'renewal') {
                        // Limpieza estructural para Renovación de Póliza
                        const { 
                            displayDoc, displayNombre, gestionType, vencimientoDate, 
                            path, id: oldId, clienteNombreCompleto,
                            estadoGestionRenovacion, 
                            canalNombreUnified, 
                            postventa, 
                            postventa_estado,
                            postventa_fechaProximaGestion,
                            postventa_numero_gestion,
                            postventa_quienGestiono,
                            postventa_ultimoTipoGestion,
                            tieneDeuda,
                            tieneSiniestro, 
                            gestionadoPor, 
                            medioPagoEmision,
                            ...cleanData 
                        } = baseData;

                        setFormData(prev => ({
                            ...prev,
                            ...cleanData,
                            type: 'patrimoniales',
                            nPoliza: '',
                            estadoEmision: '',
                            fechaCarga: new Date().toISOString().split('T')[0],
                            polizaExpirante: data.nPoliza || '',
                            companiaExpirante: data.compania || '',
                            quienRenovo: '', //
                            motivo: 'Renovación',
                            subOperacion: 'Continuidad de Cobertura', // Default para renovación
                            inicioVigencia: data.finVigencia || '',
                            finVigencia: '',
                            isPolizaPack: false,
                            referenciasPolizaPack: [],
                            primaEmitida: '', premio: '', primaPrimeraCuota: '', primaAdelanto: '',
                            pagoEfectuado: false, quienCobro: '', fechaCobro: '', fechaDespacho: '',
                            fechaRefacturacion: '', observaciones: '',
                            estadoGestionRenovacion: '',
                            gestionadoPor: '', //
                            postventa: 'No'
                        }));
                    } else {
                        // Modo Edición: Formateamos valores numéricos
                        setFormData(prev => ({ 
                            ...prev, 
                            ...baseData,
                            fechaCarga: fechaCargaOriginal,
                            primaEmitida: formatCurrencyForInput(data.primaEmitida),
                            premio: formatCurrencyForInput(data.premio),
                            primaAdelanto: formatCurrencyForInput(data.primaAdelanto),
                            primaPrimeraCuota: formatCurrencyForInput(data.primaPrimeraCuota),
                            ref_prima: formatCurrencyForInput(data.ref_prima),
                            ref_primaEmitida: formatCurrencyForInput(data.ref_primaEmitida)
                        }));
                    }
                } else if (activeClient) {
                    // Inicialización de cliente nuevo
                    setFormData(prev => ({
                        ...prev,
                        ...INITIAL_STATE,
                        type: 'patrimoniales',
                        clienteNombre: activeClient.nombre || '',
                        clienteApellido: activeClient.apellido || '',
                        clienteId: activeClient.id || '',
                        canal: activeClient.canal || '',
                        canalNombreId: activeClient.canalNombreId || '',
                        canalNombre: activeClient.canalNombre || '',
                        vendedor: activeClient.vendedor || '',
                        subcanalNombreId: activeClient.subcanalNombreId || '',
                        subcanalNombre: activeClient.subcanalNombre || '',
                        tieneContador: !!activeClient.tieneContador,
                        contadorId: activeClient.contadorId || '',
                        contadorNombre: activeClient.contadorNombre || ''
                    }));
                    if (activeClient.canalNombreId) {
                        const cFound = dbMasters.canales.find(c => c.id === activeClient.canalNombreId);
                        setCanalObj(cFound || null);
                    }
                }
                setIsHydrated(true);
            } catch (e) { 
                console.error("Error en hidratación inicial de Patrimoniales:", e); 
            }
        };

        hydrate();
    }, [initialData, activeClient, dbMasters, mode, isHydrated]);

    // --- 7. AUTOMATIZACIÓN DE BÚSQUEDA DE VINCLOS (PACKS) ---
    useEffect(() => {
        if (!formData.isPolizaPack) { 
            if (Object.keys(packInfos).length > 0) setPackInfos({}); 
            return; 
        }

        const refs = (formData.referenciasPolizaPack || []).filter(r => r.length >= 3);
        
        const timer = setTimeout(async () => {
            const nuevasInfos = { ...packInfos };
            let huboCambios = false;

            for (const nRef of refs) {
                if (!nuevasInfos[nRef]) {
                    try {
                        const q = query(collectionGroup(db, 'polizas'), where('nPoliza', '==', nRef));
                        const snap = await getDocs(q);
                        
                        if (!snap.empty) {
                            const d = snap.docs[0].data();
                            nuevasInfos[nRef] = { 
                                found: true, 
                                cliente: d.clienteNombreCompleto || `${d.clienteNombre || ''} ${d.clienteApellido || ''}`.trim() || 'SIN NOMBRE'
                            };
                        } else {
                            nuevasInfos[nRef] = { found: false };
                        }
                        huboCambios = true;
                    } catch (e) { 
                        console.error("Error buscando vínculo pack:", e); 
                    }
                }
            }

            if (huboCambios) setPackInfos(nuevasInfos);
        }, 800);

        return () => clearTimeout(timer);
    }, [formData.referenciasPolizaPack, formData.isPolizaPack, packInfos]);

    // --- 8. AUTOMATIZACIÓN DE ESTADO POR VIGENCIA ---
    useEffect(() => {
        if (!isHydrated) return;

        const { inicioVigencia, finVigencia, estadoEmision } = formData;
        const nuevoEstado = calculatePolicyStatus(inicioVigencia, finVigencia, estadoEmision);

        if (nuevoEstado !== estadoEmision) {
            setFormData(prev => ({ ...prev, estadoEmision: nuevoEstado }));
        }
    }, [formData.inicioVigencia, formData.finVigencia, formData.estadoEmision, isHydrated]);

    // --- 9. HANDLERS DE ACCIONES DE INTERFAZ ---

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }, []);

    const handleFormaPagoChange = useCallback((val) => {
        setFormData(prev => ({ 
            ...prev, 
            formaPago: val, 
            medioPago: '', 
            pagoEfectuado: false, 
            quienCobro: '', 
            fechaCobro: '', 
            nuevoBanco: '', nuevoCbu: '', nuevaMarca: '', nuevoNumero: '', nuevoVto: ''
        }));
    }, []);

    const handleTipoFacturacionChange = useCallback((val) => {
        setFormData(prev => ({ 
            ...prev, 
            tipoFacturacion: val, 
            cuotas: ''
        }));
    }, []);

    const handleCanalTypeChange = useCallback((val) => {
        setFormData(p => ({ 
            ...p, canal: val, canalNombreId: '', canalNombre: '', 
            subcanalNombreId: '', subcanalNombre: '', 
            comisionOrganizador: 0, comisionAsociado: 0,
            descuentoOrganizador: 0, descuentoAsociado: 0
        }));
        setCanalObj(null); 
        setSubcanalObj(null);
    }, []);

    const handleCanalIdChange = useCallback((id) => {
        const sel = dbMasters.canales.find(c => c.id === id);
        setFormData(p => ({ 
            ...p, canalNombreId: id, 
            canalNombre: sel?.nombreDisplay || sel?.nombre || '', 
            subcanalNombreId: '', subcanalNombre: '', 
            comisionOrganizador: 0, comisionAsociado: 0 
        }));
        setCanalObj(sel || null); 
        setSubcanalObj(null);
    }, [dbMasters.canales]);

    const handleSubcanalIdChange = useCallback((id) => {
        const sel = dbSubcanales.find(s => s.id === id);
        setFormData(p => ({ ...p, subcanalNombreId: id, subcanalNombre: sel?.nombre || '' }));
        setSubcanalObj(sel || null);
    }, [dbSubcanales]);

    const handleRiesgoChange = useCallback((val) => {
        const rFound = dbMasters.riesgos.find(x => x.nombre === val);
        setRiesgoObj(rFound || null);
        setFormData(p => ({ 
            ...p, 
            riesgo: val, 
            riesgoId: rFound ? rFound.id : '',
            subriesgo: '', 
            patentes: [''], 
            ubicacionesRiesgo: [{ direccion: '', m2: '' }], 
            actividades: [''], 
            especialidades: [''] 
        }));
    }, [dbMasters.riesgos]);

    const handleCurrencyChange = useCallback((e, fieldName) => {
        let val = e.target.value.replace(/\D/g, ''); 
        setFormData(p => ({ 
            ...p, 
            [fieldName]: val ? formatCurrencyForInput(Number(val) / 100) : '' 
        }));
    }, []);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            const result = await savePolicy({ 
                formData, 
                activeClient, 
                initialData, 
                mode, 
                tipoCarga 
            });
            if (onNotifySuccess) onNotifySuccess(result.docId, result.payload);
        } catch (error) { 
            console.error("Error crítico en el guardado de póliza:", error); 
        } finally { 
            setIsSubmitting(false); 
        }
    };

    return { 
        state: { 
            formData, dbMasters, dbSubcanales, riesgoObj, canalObj, subcanalObj, isSubmitting, mode, packInfos, ...logic 
        },
        actions: { 
            handleChange, 
            handleCanalTypeChange, 
            handleRiesgoChange, 
            handleCanalIdChange, 
            handleSubcanalIdChange, 
            handleSubmit, 
            setFormData, 
            setCanalObj, 
            setSubcanalObj,
            handleContadorToggle: (checked) => setFormData(p => ({ ...p, tieneContador: checked, contadorId: checked ? p.contadorId : '', contadorNombre: checked ? p.contadorNombre : '' })),
            handleCurrencyChange, 
            handleFormaPagoChange,
            handleTipoFacturacionChange,
            handlePackChange: (idx, val) => setFormData(p => { const n = [...(p.referenciasPolizaPack || [])]; n[idx] = val.toUpperCase().trim(); return { ...p, referenciasPolizaPack: n }; }),
            handleCompaniaChange: (val) => setFormData(p => {
                const compNormal = val.toUpperCase().trim();
                const found = dbMasters.companias.find(c => c.nombre?.toUpperCase().trim() === compNormal);
                return { 
                    ...p, 
                    compania: val, 
                    companiaId: found ? found.id : '',
                    organizador: '', 
                    pas: '' 
                };
            }),
            handleMonedaChange: (val) => setFormData(p => ({ ...p, moneda: mapMonedaToDB(val) })),
            handleRefMonedaChange: (val) => setFormData(p => ({ ...p, ref_moneda: mapMonedaToDB(val) })),
            handlePagoCheck: (checked) => setFormData(p => ({ ...p, ref_pagoEfectuado: checked, ref_estadoGestion: checked ? 'Abonado' : (p.ref_estadoGestion === 'Abonado' ? 'Revisión' : p.ref_estadoGestion) })),
            handleArrayChange: (f, i, v) => setFormData(p => { const n = [...(p[f] || [])]; n[i] = v.toUpperCase(); return { ...p, [f]: n }; }),
            addArrayItem: (f) => setFormData(p => ({ ...p, [f]: [...(p[f] || []), ''] })),
            removeArrayItem: (f, i) => setFormData(p => { const n = [...(p[f] || [])]; n.splice(i, 1); return { ...p, [f]: n.length === 0 ? [''] : n }; }),
        }
    };
};